import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import API from '@/lib/api';

const SiteSettingsContext = createContext({
  theme: null,
  brand: null,
  footer: null,
  contacts: null,
  cookieBanner: null,
  homepageBlocks: [],
  loading: true,
  refresh: () => {},
});

// Convert "#A0522D" → "160 53% 40%" (approx HSL) for CSS var substitution.
// We just use the raw hex for both Tailwind utility custom classes and as
// CSS variables. The existing palette in tailwind.config uses HSL but we
// override via CSS variables which take precedence at runtime.
function applyTheme(theme) {
  if (!theme) return;
  const root = document.documentElement;
  const colorMap = {
    color_brick: '--color-brick',
    color_forest: '--color-forest',
    color_clay: '--color-clay',
    color_stone_text: '--color-stone-text',
    color_paper: '--color-paper',
  };
  Object.entries(colorMap).forEach(([k, cssVar]) => {
    if (theme[k]) root.style.setProperty(cssVar, theme[k]);
  });
  if (theme.font_serif) root.style.setProperty('--font-serif', `"${theme.font_serif}", serif`);
  if (theme.font_sans) root.style.setProperty('--font-sans', `"${theme.font_sans}", sans-serif`);

  // Inject Google Fonts dynamically if needed
  const fonts = [theme.font_serif, theme.font_sans].filter(Boolean);
  fonts.forEach(fontName => {
    const linkId = `gf-${fontName.replace(/\s/g, '-')}`;
    if (document.getElementById(linkId)) return;
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  });
}

export function SiteSettingsProvider({ children }) {
  const [data, setData] = useState({ theme: null, brand: null, footer: null, contacts: null, cookieBanner: null, homepageBlocks: [], loading: true });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await API.get('/site-settings');
      const { theme, brand, footer, contacts, cookie_banner, homepage_blocks } = res.data;
      applyTheme(theme);
      // Update document title
      if (brand?.site_name) document.title = `${brand.site_name} — ${brand.tagline || ''}`.trim();
      setData({ theme, brand, footer, contacts, cookieBanner: cookie_banner || null, homepageBlocks: homepage_blocks || [], loading: false });
    } catch (err) {
      console.warn('Site settings unavailable, using defaults', err);
      setData(d => ({ ...d, loading: false }));
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Memoize the context value so consumers aren't invalidated on every render —
  // in React 19 the recreated object reference cascades through Suspense and
  // prevents lazy() imports from resolving. See troubleshoot iter 2026-02.
  const contextValue = useMemo(
    () => ({ ...data, refresh: fetchSettings }),
    [data, fetchSettings],
  );

  return (
    <SiteSettingsContext.Provider value={contextValue}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export const useSiteSettings = () => useContext(SiteSettingsContext);
